import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Search } from 'lucide-react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Autoplay } from 'swiper/modules';
import { RoughNotation } from 'react-rough-notation';
import { useNavigate } from 'react-router-dom';
import 'swiper/css';
import 'swiper/css/navigation';

const NewsAndBlogs = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [selectedType, setSelectedType] = useState('all');
  const [selectedYear, setSelectedYear] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPages, setCurrentPages] = useState({});
  const [totalPages, setTotalPages] = useState({});
  const postsPerPage = 4;
  const [imageLoading, setImageLoading] = useState({});

  const BASE_URL = process.env.REACT_APP_BASE_URL;

  useEffect(() => {
    fetch(BASE_URL+'/api/posts')
      .then(response => response.json())
      .then(data => {
        setPosts(data);
        // Initialize pagination for each post type
        console.log(data);
        const types = [...new Set(data.map(post => post.type))];
        const pagesObj = types.reduce((acc, type) => ({ ...acc, [type]: 1 }), {});
        setCurrentPages(pagesObj);
        
        // Calculate total pages for each type
        const totalPagesObj = types.reduce((acc, type) => ({
          ...acc,
          [type]: Math.ceil(data.filter(post => post.type === type).length / postsPerPage)
        }), {});
        setTotalPages(totalPagesObj);
      })
      .catch(error => console.error('Error fetching posts:', error));
  }, []);

  const filteredPosts = posts.filter(post => {
    if (selectedType !== 'all' && post.type !== selectedType) return false;
    if (selectedYear !== 'all' && new Date(post.createdAt).getFullYear().toString() !== selectedYear) return false;
    if (searchTerm && !post.title.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return true;
  });

  const handlePageChange = (type, page) => {
    setCurrentPages(prev => ({ ...prev, [type]: page }));
  };

  const renderPagination = (type, totalPages) => {
    const currentPage = currentPages[type] || 1;
    const pages = [];
    
    for (let i = 1; i <= totalPages; i++) {
      if (
        i === 1 ||
        i === totalPages ||
        (i >= currentPage - 1 && i <= currentPage + 1)
      ) {
        pages.push(i);
      } else if (i === currentPage - 2 || i === currentPage + 2) {
        pages.push('...');
      }
    }

    return (
      <div className="flex justify-center mt-4 gap-2">
        {pages.map((num, idx) => (
          <button
            key={`${type}-${num}-${idx}`}
            onClick={() => typeof num === 'number' && handlePageChange(type, num)}
            className={`w-8 h-8 rounded-full ${
              num === currentPage ? 'bg-purple-800 text-white' : 'bg-gray-200'
            } ${typeof num !== 'number' ? 'cursor-default' : 'cursor-pointer'}`}
            disabled={typeof num !== 'number'}
          >
            {num}
          </button>
        ))}
      </div>
    );
  };

  const handlePostClick = (postId) => {
    navigate(`/viewpost/${postId}`);
  };

  const handleImageLoad = (postId) => {
    setImageLoading(prev => ({ ...prev, [postId]: false }));
  };

  const renderPosts = (type) => {
    const typeFilteredPosts = filteredPosts.filter(post => post.type === type);

    return (
      <div className="w-full py-8">
        <div className="relative flex flex-col items-center mb-12">
          <div className="w-full h-[1px] bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
          <div className="py-8 px-12">
            <h2 className="text-4xl font-arial-rounded font-bold text-center text-primary capitalize">
              {type}
            </h2>
          </div>
          <div className="w-full h-[1px] bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
        </div>

        <Swiper
          modules={[Navigation, Autoplay]}
          spaceBetween={30}
          slidesPerView={1}
          navigation
          autoplay={{ delay: 5000, disableOnInteraction: false }}
          breakpoints={{
            640: { slidesPerView: 2 },
            1024: { slidesPerView: 3 },
            1280: { slidesPerView: 4 }
          }}
          className="py-4"
        >
          {typeFilteredPosts.map((post) => (
            <SwiperSlide key={post._id}>
              <div 
                onClick={() => handlePostClick(post.id)}
                className="bg-white rounded-xl mb-6 overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 cursor-pointer group"
              >
                <div className="relative overflow-hidden">
                  {/* Loading skeleton */}
                  <div className={`absolute inset-0 bg-gray-200 animate-pulse ${!imageLoading[post._id] ? 'hidden' : ''}`} />
                  
                  <img
                    src={post.titleImage}
                    alt={post.title}
                    className={`w-full h-56 object-cover transition-transform duration-300 group-hover:scale-110 ${imageLoading[post._id] ? 'opacity-0' : 'opacity-100'}`}
                    onLoad={() => handleImageLoad(post._id)}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-primary mb-3 group-hover:text-purple-700 transition-colors">
                    {post.title}
                  </h3>
                  <p className="text-base text-gray-600 line-clamp-3">
                    {post.content}
                  </p>
                  <div className="mt-4 flex justify-between items-center">
                    <span className="text-sm text-primary/70">
                      {new Date(post.createdAt).toLocaleDateString()}
                    </span>
                    <span className="text-sm text-primary font-semibold">
                      Read More →
                    </span>
                  </div>
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    );
  };

  // Get unique post types from the posts array
  const postTypes = [...new Set(posts.map(post => post.type))];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
      <div className="mb-20">
        <h1 className="text-5xl md:text-6xl font-bold text-primary text-center mb-6">
          <RoughNotation 
            type="highlight" 
            show={true}
            color="rgba(85, 11, 96, 0.2)"
            animationDuration={800}
            iterations={1}
            multiline={true}
          >
            Latest News & Updates
          </RoughNotation>
        </h1>
        <p className="text-center text-gray-600 text-lg max-w-2xl mx-auto">
          Stay informed about our latest developments, announcements, and community updates
        </p>
      </div>
      
      <div className="flex flex-wrap gap-4 md:gap-6 justify-center mb-16">
        <div className="relative flex-grow max-w-md">
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-primary" size={24} />
          <input
            type="text"
            className="w-full pl-12 pr-6 py-3 text-lg border-2 border-primary/30 rounded-full focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all"
            placeholder="Search posts..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
        <select
          value={selectedType}
          onChange={(e) => setSelectedType(e.target.value)}
          className="px-6 py-3 text-lg border-2 border-primary rounded-full focus:outline-none focus:ring-2 focus:ring-primary/50"
        >
          <option value="all">News Type</option>
          {postTypes.map(type => (
            <option key={type} value={type}>
              {type ? type.charAt(0).toUpperCase() + type.slice(1) : ''}
            </option>
          ))}
        </select>
        
        <select
          value={selectedYear}
          onChange={(e) => setSelectedYear(e.target.value)}
          className="px-6 py-3 text-lg border-2 border-primary rounded-full focus:outline-none focus:ring-2 focus:ring-primary/50"
        >
          <option value="all">Year</option>
          <option value="2025">2025</option>
          <option value="2024">2024</option>
        </select>
        
        <button 
          className="px-8 py-3 text-lg bg-primary text-white rounded-full hover:bg-primary/90 transition-all duration-300 hover:shadow-lg transform hover:-translate-y-0.5"
          onClick={() => {
            setSelectedType('all');
            setSelectedYear('all');
            setSearchTerm('');
          }}
        >
          Reset Filters
        </button>
      </div>

      <div className="space-y-4">
        {postTypes.map(type => (
          selectedType === 'all' || selectedType === type ? 
            <div key={type}>
              {renderPosts(type)}
            </div> : null
        ))}
      </div>
    </div>
  );
};

export default NewsAndBlogs;